import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Link as LinkIcon, Mail, MapPin, Phone, Search, User2 } from 'lucide-react';
import AuthContext from '../context/AuthContext';
import axiosInstance from '../axiosConfig';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '../components/ui/card';
import { cn } from '../lib/utils';

const palette = {
  background: 'bg-slate-50',
  textPrimary: 'text-slate-900',
  textSecondary: 'text-slate-600'
};

const Home = () => {
  const { token, user } = useContext(AuthContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [cards, setCards] = useState([]);
  const [filteredCards, setFilteredCards] = useState([]);
  const [copiedCardId, setCopiedCardId] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const navigate = useNavigate();

  const getStaticUrl = useMemo(() => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5050';
    return (cardId) => `${baseUrl}/api/static/${cardId}/${cardId}.html`;
  }, []);

  useEffect(() => {
    if (!token || user?.role === 'superadmin') {
      return;
    }

    const fetchCards = async () => {
      setIsFetching(true);
      try {
        const response = await axiosInstance.get('/api/cards', {
          headers: { 'x-auth-token': token }
        });
        setCards(response.data);
      } catch (error) {
        console.error('Error fetching cards:', error);
      } finally {
        setIsFetching(false);
      }
    };

    fetchCards();
  }, [token, user]);

  useEffect(() => {
    const keywords = searchTerm
      .toLowerCase()
      .split(' ')
      .map((keyword) => keyword.trim())
      .filter(Boolean);

    if (keywords.length === 0) {
      setFilteredCards(cards);
      return;
    }

    const results = cards.filter((card) =>
      keywords.every((keyword) => {
        const contactMatches = Object.values(card.contactInfo || {})
          .join(' ')
          .toLowerCase()
          .includes(keyword);

        const customMatches = (card.customFields || []).some((field) =>
          field.value?.toLowerCase().includes(keyword)
        );

        return contactMatches || customMatches;
      })
    );

    setFilteredCards(results);
  }, [searchTerm, cards]);

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo biglietto?')) {
      return;
    }

    try {
      await axiosInstance.delete(`/api/cards/${id}`, {
        headers: { 'x-auth-token': token }
      });
      setCards((prev) => prev.filter((card) => card._id !== id));
      setFilteredCards((prev) => prev.filter((card) => card._id !== id));
    } catch (error) {
      console.error('Error deleting card:', error);
    }
  };

  const handleCopyLink = async (cardId) => {
    try {
      await navigator.clipboard.writeText(getStaticUrl(cardId));
      setCopiedCardId(cardId);
      setTimeout(() => setCopiedCardId(null), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const totalCards = cards.length;
  const { cardsWithLinks, organizationsCount } = useMemo(() => {
    const withLinks = cards.filter((card) =>
      (card.customFields || []).some((field) => field.label && field.value)
    ).length;

    const organizations = new Set(
      cards
        .map((card) => card.contactInfo?.organization)
        .filter((org) => Boolean(org))
    ).size;

    return { cardsWithLinks: withLinks, organizationsCount: organizations };
  }, [cards]);

  const displayCards = filteredCards;

  useEffect(() => {
    if (user?.role === 'superadmin') {
      navigate('/admin', { replace: true });
    }
  }, [user, navigate]);

  if (user?.role === 'superadmin') {
    return null;
  }

  return (
    <div className={cn('min-h-screen pb-16', palette.background)}>
      <div className="container mx-auto space-y-8 px-4 py-8 lg:space-y-12">
        <div className="grid gap-6 lg:grid-cols-[3fr_2fr]">
          <Card className="overflow-hidden border border-slate-200 bg-white shadow-sm">
            <CardHeader className="space-y-4">
              <div className="space-y-2">
                <CardTitle className={cn('text-3xl font-semibold', palette.textPrimary)}>
                  La tua rubrica digitale
                </CardTitle>
                <CardDescription className={palette.textSecondary}>
                  Organizza i contatti, crea nuovi biglietti e condividili con un click.
                </CardDescription>
              </div>
              <div className="relative">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                <Input
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Cerca per nome, email, telefono o link personalizzato"
                  className="h-12 rounded-2xl border-slate-200 bg-slate-50 pl-12 text-slate-700 shadow-inner placeholder:text-slate-400"
                />
              </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-3 md:flex-row">
              <Button onClick={() => navigate('/create-card')} className="shadow-sm">
                Crea nuovo biglietto
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/bulk-create')}
                className="border-slate-200 text-slate-600 hover:bg-slate-100"
              >
                Importa da CSV
              </Button>
            </CardContent>
          </Card>

          <Card className="border border-slate-200 bg-white shadow-sm">
            <CardHeader>
              <CardTitle className={cn('text-xl font-semibold', palette.textPrimary)}>
                Panoramica rapida
              </CardTitle>
              <CardDescription className={palette.textSecondary}>
                Numeri utili per capire l’andamento del tuo account.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-sm">
                <p className="text-xs uppercase tracking-wide text-slate-400">Totale biglietti</p>
                <p className="mt-2 text-2xl font-semibold text-slate-800">{totalCards}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-sm">
                <p className="text-xs uppercase tracking-wide text-slate-400">Con link personalizzati</p>
                <p className="mt-2 text-2xl font-semibold text-slate-800">{cardsWithLinks}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-sm">
                <p className="text-xs uppercase tracking-wide text-slate-400">Organizzazioni presenti</p>
                <p className="mt-2 text-2xl font-semibold text-slate-800">{organizationsCount}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-sm">
                <p className="text-xs uppercase tracking-wide text-slate-400">Utente collegato</p>
                <div className="mt-2 flex items-center gap-2 text-sm text-slate-600">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                    <User2 className="h-4 w-4" />
                  </span>
                  <span className="font-medium text-slate-700">{user?.email}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {displayCards.map((card) => {
            const { contactInfo = {}, customFields = [] } = card;
            const fullName = [contactInfo.name, contactInfo.surname].filter(Boolean).join(' ') || 'Senza nome';

            return (
              <Card
                key={card._id}
                className="flex h-full flex-col justify-between rounded-2xl border border-slate-800 bg-slate-900 text-slate-100 shadow-xl transition hover:-translate-y-1 hover:shadow-2xl"
              >
                <CardHeader className="space-y-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <CardTitle className="text-xl font-semibold text-white">{fullName}</CardTitle>
                    {contactInfo.organization && (
                      <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white">
                        {contactInfo.organization}
                      </span>
                    )}
                  </div>
                  {contactInfo.jobTitle && (
                    <CardDescription className="text-sm text-slate-300">
                      {contactInfo.jobTitle}
                    </CardDescription>
                  )}
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="space-y-2 text-sm text-slate-300">
                    {contactInfo.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-slate-400" />
                        <a
                          href={`mailto:${contactInfo.email}`}
                          className="font-medium text-blue-300 hover:underline"
                        >
                          {contactInfo.email}
                        </a>
                      </div>
                    )}
                    {contactInfo.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-slate-400" />
                        <a
                          href={`tel:${contactInfo.phone}`}
                          className="font-medium text-blue-300 hover:underline"
                        >
                          {contactInfo.phone}
                        </a>
                      </div>
                    )}
                    {contactInfo.address && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-slate-400" />
                        <span>{contactInfo.address}</span>
                      </div>
                    )}
                  </div>

                  {customFields.length > 0 && (
                    <div className="space-y-2 text-sm">
                      <p className="font-semibold text-white">Link rapidi</p>
                      <div className="space-y-1">
                        {customFields.map((field) => {
                          if (!field.value) return null;
                          const href = field.value.startsWith('http') ? field.value : `http://${field.value}`;
                          return (
                            <a
                              key={field._id || field.value}
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm transition hover:border-blue-400 hover:bg-blue-500/20"
                            >
                              <LinkIcon className="h-4 w-4 text-blue-300" />
                              <span className="truncate text-slate-200">
                                <span className="font-medium text-white">{field.label || 'Link'}:</span>{' '}
                                {field.value}
                              </span>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </CardContent>

                <CardFooter className="flex flex-wrap gap-2 border-t border-white/10 bg-white/5 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/edit/${card._id}`)}
                    className="border-white/20 text-slate-100 hover:bg-white/10"
                  >
                    Modifica
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => window.open(getStaticUrl(card._id), '_blank', 'noopener,noreferrer')}
                    className="border-white/20 text-slate-100 hover:bg-white/10"
                  >
                    Apri card
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleCopyLink(card._id)}
                    className={cn(
                      'border-white/20 text-slate-100 hover:bg-white/10',
                      copiedCardId === card._id && 'border-emerald-400 bg-emerald-600/30 text-emerald-200'
                    )}
                  >
                    <Copy className="mr-2 h-4 w-4" />
                    {copiedCardId === card._id ? 'Copiato!' : 'Copia link'}
                  </Button>
                  <Button variant="destructive" onClick={() => handleDelete(card._id)}>
                    Elimina
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {isFetching && (
          <Card className="border border-dashed border-slate-300 bg-slate-50">
            <CardContent className="flex flex-col items-center gap-3 py-16 text-center text-sm text-slate-500">
              Caricamento dei tuoi biglietti in corso…
            </CardContent>
          </Card>
        )}

        {displayCards.length === 0 && !isFetching && (
          <Card className="border border-dashed border-slate-300 bg-white">
            <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
              <p className="text-lg font-semibold text-slate-700">Nessun biglietto trovato</p>
              <p className="max-w-md text-sm text-slate-500">
                Prova a cambiare i termini di ricerca oppure crea un nuovo biglietto digitale per iniziare a condividere i tuoi contatti.
              </p>
              <Button onClick={() => navigate('/create-card')}>Crea il primo biglietto</Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Home;
